/**
 * Vital Signs Analysis API Service
 * ================================
 * 
 * This service provides methods to interact with the vital signs analysis API
 * that processes recordings and returns calculated vital signs.
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

class VitalSignsAnalysisAPI {
  constructor() {
    this.baseURL = `${API_BASE_URL}/api/vital-signs-analysis-simple`;
  }

  /**
   * Test the algorithms with synthetic data
   * @returns {Promise<Object>} Test results with calculated vital signs
   */
  async testAlgorithms() {
    try {
      const response = await fetch(`${this.baseURL}/test-algorithms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error testing algorithms:', error);
      throw error;
    }
  }

  /**
   * Analyze an existing recording session
   * @param {string} sessionId - The session ID to analyze
   * @returns {Promise<Object>} Analysis results with vital signs
   */
  async analyzeExistingSession(sessionId) {
    // 🎯 TIMEOUT & PERFORMANCE LOGGING - Priority 1
    const requestStartTime = Date.now();
    const timeoutThreshold = 120000; // 2 minutes (typical mobile timeout)
    const performanceWarningThreshold = 60000; // 1 minute warning
    
    try {
      console.log(`🔬 Frontend: Starting analysis for session ${sessionId}`);
      console.log(`   ⏱️  PERFORMANCE TRACKING: Request started at ${new Date(requestStartTime).toISOString()}`);
      console.log(`   📱 MOBILE TIMEOUT MONITORING: Will timeout after ${timeoutThreshold/1000}s`);
      console.log(`   ⚠️  PERFORMANCE WARNING: Will warn after ${performanceWarningThreshold/1000}s`);
      console.log(`   📡 Calling API: ${this.baseURL}/analyze-existing-session`);
      console.log(`   🔑 Session ID being sent: "${sessionId}"`);
      console.log(`   🔑 Session ID type: ${typeof sessionId}`);
      console.log(`   🔑 Session ID length: ${sessionId ? sessionId.length : 'null'}`);
      
      const formData = new FormData();
      formData.append('session_id', sessionId);

      console.log(`   📤 FormData contents:`);
      for (let [key, value] of formData.entries()) {
        console.log(`      ${key}: "${value}"`);
      }

      // 🎯 NETWORK QUALITY & TIMEOUT MONITORING
      const fetchStartTime = Date.now();
      console.log(`   🌐 NETWORK MONITORING: Starting fetch at ${new Date(fetchStartTime).toISOString()}`);
      
      const response = await fetch(`${this.baseURL}/analyze-existing-session`, {
        method: 'POST',
        body: formData,
      });

      const fetchEndTime = Date.now();
      const fetchDuration = fetchEndTime - fetchStartTime;
      console.log(`   🌐 NETWORK MONITORING: Fetch completed in ${fetchDuration}ms`);
      console.log(`   📥 Response status: ${response.status}`);
      console.log(`   📥 Response headers:`, Object.fromEntries(response.headers.entries()));

      // 🎯 MOBILE TIMEOUT ANALYSIS
      if (fetchDuration > performanceWarningThreshold) {
        console.warn(`⚠️  MOBILE PERFORMANCE WARNING: Fetch took ${fetchDuration}ms (>${performanceWarningThreshold/1000}s)`);
        console.warn(`   📱 MOBILE RISK: Request may timeout on slow mobile connections`);
      }

      if (!response.ok) {
        const requestDuration = Date.now() - requestStartTime;
        console.error(`❌ Frontend: Analysis API failed with status ${response.status}`);
        console.error(`   ⏱️  FAILURE TIMING: Request failed after ${requestDuration}ms`);
        const errorText = await response.text();
        console.error(`❌ Frontend: Error response body:`, errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      // 🎯 RESPONSE PARSING PERFORMANCE MONITORING
      const parseStartTime = Date.now();
      const results = await response.json();
      const parseDuration = Date.now() - parseStartTime;
      
      const totalRequestDuration = Date.now() - requestStartTime;
      console.log(`✅ Frontend: Analysis completed successfully for session ${sessionId}`);
      console.log(`   ⏱️  TOTAL REQUEST TIME: ${totalRequestDuration}ms`);
      console.log(`   🌐 Network time: ${fetchDuration}ms`);
      console.log(`   📊 Parse time: ${parseDuration}ms`);
      console.log(`   📊 Raw API response:`, results);
      
      // 🎯 MOBILE TIMEOUT RISK ASSESSMENT
      if (totalRequestDuration > timeoutThreshold) {
        console.error(`🚨 MOBILE TIMEOUT EXCEEDED: Request took ${totalRequestDuration}ms (>${timeoutThreshold/1000}s)`);
        console.error(`   📱 MOBILE IMPACT: This request would timeout on most mobile browsers`);
        console.error(`   💡 SOLUTION: Need to optimize backend processing or implement streaming`);
      } else if (totalRequestDuration > performanceWarningThreshold) {
        console.warn(`⚠️  MOBILE PERFORMANCE WARNING: Request took ${totalRequestDuration}ms`);
        console.warn(`   📱 MOBILE RISK: Close to timeout threshold (${timeoutThreshold/1000}s)`);
      }
      
      // 🔍 DEBUG: Detailed response analysis
      console.log(`   🔍 Response structure analysis:`);
      console.log(`      📋 Response type:`, typeof results);
      console.log(`      🔑 Top-level keys:`, Object.keys(results));
      console.log(`      🎯 vital_signs present:`, !!results.vital_signs);
      console.log(`      📊 vital_signs type:`, typeof results.vital_signs);
      
      if (results.vital_signs) {
        console.log(`      🔍 vital_signs keys:`, Object.keys(results.vital_signs));
        console.log(`      🔍 vital_signs values:`, results.vital_signs);
        console.log(`      ❤️  heart_rate:`, results.vital_signs.heart_rate, `(type: ${typeof results.vital_signs.heart_rate})`);
        console.log(`      💓 hrv:`, results.vital_signs.hrv, `(type: ${typeof results.vital_signs.hrv})`);
        console.log(`      📈 rhythm_regularity:`, results.vital_signs.rhythm_regularity, `(type: ${typeof results.vital_signs.rhythm_regularity})`);
      } else {
        console.log(`      ⚠️  No vital_signs in response`);
        console.log(`      🔍 Full response for debugging:`, JSON.stringify(results, null, 2));
      }
      
      return results;
    } catch (error) {
      // 🎯 ERROR TIMING & MOBILE IMPACT ANALYSIS
      const errorTime = Date.now();
      const requestDuration = errorTime - requestStartTime;
      
      console.error('❌ Frontend: Error analyzing existing session:', error);
      console.error('❌ Frontend: Error details:', {
        message: error.message,
        stack: error.stack,
        sessionId: sessionId,
        // 🎯 TIMEOUT & PERFORMANCE ERROR LOGGING
        requestDuration: `${requestDuration}ms`,
        timeoutExceeded: requestDuration > timeoutThreshold,
        mobileImpact: requestDuration > performanceWarningThreshold ? 'HIGH' : 'LOW',
        errorTiming: {
          requestStarted: new Date(requestStartTime).toISOString(),
          errorOccurred: new Date(errorTime).toISOString(),
          durationMs: requestDuration,
          exceedsTimeout: requestDuration > timeoutThreshold,
          mobileTimeout: `${timeoutThreshold/1000}s`,
          performanceWarning: `${performanceWarningThreshold/1000}s`
        }
      });
      
      // 🎯 MOBILE-SPECIFIC ERROR ANALYSIS
      if (requestDuration > timeoutThreshold) {
        console.error(`🚨 MOBILE DIAGNOSIS: Error occurred after ${requestDuration}ms`);
        console.error(`   📱 LIKELY CAUSE: Mobile browser timeout (typical limit: ${timeoutThreshold/1000}s)`);
        console.error(`   💡 SOLUTION: Backend processing takes too long for mobile devices`);
      }
      
      throw error;
    }
  }

  /**
   * Get quality assessment for a recording session
   * @param {string} sessionId - The session ID to assess
   * @returns {Promise<Object>} Quality assessment results
   */
  async getSessionQuality(sessionId) {
    try {
      const response = await fetch(`${this.baseURL}/session-quality/${sessionId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting session quality:', error);
      throw error;
    }
  }

  /**
   * Analyze uploaded recordings
   * @param {string} sessionId - The session ID
   * @param {File} videoFile - Video recording file (optional)
   * @param {File} audioFile - Audio recording file (optional)
   * @returns {Promise<Object>} Analysis results with vital signs
   */
  async analyzeRecording(sessionId, videoFile = null, audioFile = null) {
    try {
      const formData = new FormData();
      formData.append('session_id', sessionId);
      
      if (videoFile) {
        formData.append('video_file', videoFile);
      }
      
      if (audioFile) {
        formData.append('audio_file', audioFile);
      }

      const response = await fetch(`${this.baseURL}/analyze-recording`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error analyzing recording:', error);
      throw error;
    }
  }

  /**
   * Check the health of the vital signs analysis service
   * @returns {Promise<Object>} Health status
   */
  async healthCheck() {
    try {
      const response = await fetch(`${this.baseURL}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error checking health:', error);
      throw error;
    }
  }

  /**
   * Process stored recordings and get vital signs
   * @param {string} sessionId - The session ID
   * @param {Array} storedRecordings - Array of stored recording objects
   * @returns {Promise<Object>} Processed vital signs results
   */
  async processStoredRecordings(sessionId, storedRecordings) {
    try {
      console.log(`🚀 Frontend: Processing stored recordings for session ${sessionId}`);
      console.log(`   📁 Recordings count: ${storedRecordings.length}`);
      console.log(`   📊 Recording types:`, storedRecordings.map(r => r.type));
      console.log(`   🔑 Session ID type: ${typeof sessionId}`);
      console.log(`   🔑 Session ID value: "${sessionId}"`);
      
      // First, try to analyze the existing session
      console.log(`🔬 Frontend: Attempting to analyze existing session...`);
      console.log(`   📡 Calling analyzeExistingSession with sessionId: "${sessionId}"`);
      
      const analysisResults = await this.analyzeExistingSession(sessionId);
      console.log(`   📥 analyzeExistingSession returned:`, analysisResults);
      console.log(`   🔍 analysisResults type: ${typeof analysisResults}`);
      console.log(`   🔍 analysisResults keys: ${analysisResults ? Object.keys(analysisResults) : 'null'}`);
      
      // 🔧 FIX: Return the raw API response, let the component handle conversion
      console.log(`✅ Frontend: Returning raw API response for component processing`);
      return analysisResults;
      
    } catch (error) {
      console.error(`❌ Frontend: Error processing stored recordings:`, error);
      console.error(`   🔍 Error details:`, {
        message: error.message,
        stack: error.stack,
        sessionId,
        recordingsCount: storedRecordings.length
      });
      
      // NO FALLBACK: Throw the original error instead of using fake test results
      console.error(`❌ Frontend: No fallback used - throwing original error to prevent fake results`);
      console.error(`❌ Frontend: Original error details:`, {
        message: error.message,
        stack: error.stack,
        sessionId,
        recordingsCount: storedRecordings.length
      });
      
      // Throw the original error with additional context
      throw new Error(
        `Failed to get real vital signs results for session ${sessionId}: ${error.message}. ` +
        `No fallback values will be provided to ensure data accuracy.`
      );
    }
  }

  /**
   * Convert API response format to component's expected format
   * @param {Object} apiResponse - Response from the API
   * @returns {Object} Formatted results for the component
   */
  convertAPIResponseToResults(apiResponse) {
    console.log('🔍 Frontend: Starting API response conversion...');
    console.log('   📥 Raw API response:', apiResponse);
    
    // Handle different response formats
    let vitalSigns = {};
    
    if (apiResponse.vital_signs) {
      // Direct vital signs response
      vitalSigns = apiResponse.vital_signs;
      console.log('   ✅ Using direct vital_signs:', vitalSigns);
    } else if (apiResponse.test_results && apiResponse.test_results.combined_results) {
      // Test results response
      vitalSigns = apiResponse.test_results.combined_results;
      console.log('   ✅ Using test_results.combined_results:', vitalSigns);
    } else {
      // Fallback to empty vital signs
      vitalSigns = {};
      console.log('   ⚠️  No vital signs found, using empty object');
    }

    // Debug: Log the vital signs object structure
    console.log('   🔍 Vital signs object keys:', Object.keys(vitalSigns));
    console.log('   🔍 Vital signs object values:', vitalSigns);

    // Helper function to clean numeric values - now handles 'N/A' properly
    const cleanNumericValue = (value, fieldName = 'unknown') => {
      console.log(`   🔧 Cleaning ${fieldName}:`, value, 'Type:', typeof value);
      
      // Handle 'N/A' values from backend
      if (value === 'N/A' || value === 'n/a') {
        console.log(`   ℹ️  ${fieldName} is 'N/A' from backend - preserving as 'N/A'`);
        return 'N/A';
      }
      
      if (value === null || value === undefined) {
        console.log(`   ⚠️  ${fieldName} is null/undefined, returning 'N/A'`);
        return 'N/A';
      }
      
      if (typeof value === 'number') {
        if (isNaN(value) || !isFinite(value)) {
          console.log(`   ⚠️  ${fieldName} is NaN or infinite, returning 'N/A'`);
          return 'N/A';
        }
        const result = parseFloat(value.toFixed(2));
        console.log(`   ✅ ${fieldName} converted to:`, result);
        return result;
      } else if (typeof value === 'string') {
        const parsed = parseFloat(value);
        if (isNaN(parsed)) {
          console.log(`   ⚠️  ${fieldName} string cannot be parsed, returning 'N/A'`);
          return 'N/A';
        }
        const result = parseFloat(parsed.toFixed(2));
        console.log(`   ✅ ${fieldName} string parsed to:`, result);
        return result;
      } else {
        console.log(`   ⚠️  ${fieldName} has unexpected type ${typeof value}, returning 'N/A'`);
        return 'N/A';
      }
    };

    // Convert to the format expected by the component with cleaned values
    // Don't provide default fallback values - let cleanNumericValue handle missing data
    const convertedResults = {
      heartRate: cleanNumericValue(vitalSigns.heart_rate || vitalSigns.heartRate, 'heart_rate'),
      bloodPressure: {
        systolic: cleanNumericValue(vitalSigns.blood_pressure?.systolic || vitalSigns.bloodPressure?.systolic, 'systolic'),
        diastolic: cleanNumericValue(vitalSigns.blood_pressure?.diastolic || vitalSigns.bloodPressure?.diastolic, 'diastolic'),
        map: cleanNumericValue(vitalSigns.blood_pressure?.map || vitalSigns.bloodPressure?.map, 'map')
      },
      heartRateVariability: cleanNumericValue(vitalSigns.hrv || vitalSigns.heartRateVariability, 'hrv'),
      rhythmRegularity: vitalSigns.rhythm_regularity || vitalSigns.rhythmRegularity || 'N/A', // Keep as string - don't convert to number
      audioQuality: cleanNumericValue(apiResponse.overall_quality, 'overall_quality'),
      recordingDuration: cleanNumericValue(apiResponse.quality_metrics?.video?.duration, 'recording_duration'),
      segmentsCompleted: 3,
      confidence: cleanNumericValue(apiResponse.confidence_scores?.overall, 'confidence'),
      recommendations: apiResponse.recommendations || [],
      analysisStatus: apiResponse.analysis_status || 'completed'
    };

    console.log('   🔄 Converted results:', convertedResults);
    console.log('   ✅ Conversion completed successfully');
    
    return convertedResults;
  }

  /**
   * Test the conversion logic with sample data
   * This helps debug issues with the conversion process
   */
  // testConversionLogic() {
  //   console.log('🧪 Frontend: Testing conversion logic with sample data...');
    
    // Sample data that matches the backend response structure
    // const sampleBackendResponse = {
    //   session_id: 'test_session_123',
    //   timestamp: '2025-08-13T10:42:21.203892',
    //   analysis_status: 'completed',
    //   vital_signs: {
    //     heart_rate: 75.0,
    //     blood_pressure: {
    //       systolic: 120.0,
    //       diastolic: 80.0,
    //       map: 93.0
    //     },
    //     hrv: 50.0,                    // This should convert to heartRateVariability
    //     rhythm_regularity: 85.0       // This should convert to rhythmRegularity
    //   },
    //   overall_quality: 80,
    //   confidence_scores: { overall: 75 },
    //   recommendations: ['Test recommendation']
    // };
    
    // console.log('   📥 Sample backend response:', sampleBackendResponse);
    
    // // Test the conversion
    // const convertedResults = this.convertAPIResponseToResults(sampleBackendResponse);
    
    // console.log('   🔄 Converted results:', convertedResults);
    // console.log('   💓 HRV conversion test:', {
    //   input: sampleBackendResponse.vital_signs.hrv,
    //   output: convertedResults.heartRateVariability,
    //   expected: 50.0
    // });
    // console.log('   📈 Rhythm conversion test:', {
    //   input: sampleBackendResponse.vital_signs.rhythm_regularity,
    //   output: convertedResults.rhythmRegularity,
    //   expected: 85.0
    // });
    
    // Verify the conversion worked correctly
  //   const hrvCorrect = convertedResults.heartRateVariability === 50.0;
  //   const rhythmCorrect = convertedResults.rhythmRegularity === 85.0;
    
  //   console.log(`   ✅ HRV conversion: ${hrvCorrect ? 'PASS' : 'FAIL'}`);
  //   console.log(`   ✅ Rhythm conversion: ${rhythmCorrect ? 'PASS' : 'FAIL'}`);
    
  //   return {
  //     success: hrvCorrect && rhythmCorrect,
  //     hrvCorrect,
  //     rhythmCorrect,
  //     convertedResults
  //   };
  // }
}

// Create and export a singleton instance
export const vitalSignsAnalysisAPI = new VitalSignsAnalysisAPI();
export default vitalSignsAnalysisAPI;
